import dayjs from 'dayjs';

import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/ar'; // Import the Arabic locale

// ----------------------------------------------------------------------

dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.locale('en');

/**
 * Docs: https://day.js.org/docs/en/display/format
 */

export function formatDateTime(date, format = 'D MMMM YYYY, h:mm A') {
  if (!date) {
    console.log('No date provided:', date);
    return 'Not Available';
  }

  const parsedDate = dayjs(date);
  if (!parsedDate.isValid()) {
    return 'Invalid time value';
  }

  return parsedDate.format(format);
}

export function formatTime(date, format = 'h:mm A') {
  if (!date) {
    console.log('No date provided:', date);
    return 'Not Available';
  }

  const parsedDate = dayjs(date);
  if (!parsedDate.isValid()) {
    return 'Invalid time value';
  }

  return parsedDate.format(format);
}

export function formatDateTimeForPayload(date, format = 'YYYY-MM-DDTHH:mm:ss.sssZ') {
  if (!date || date === '') {
    console.log('No valid date provided:', date);
    return null;
  }

  const parsedDate = dayjs(date);
  if (!parsedDate.isValid()) {
    return null;
  }

  return parsedDate.format(format);
}

export function formatDate(date, format = 'YYYY-MM-DD') {
  if (!date) {
    console.log('No date provided:', date);
    return '';
  }

  const parsedDate = dayjs(date);
  if (!parsedDate.isValid()) {
    return 'Invalid time value';
  }

  return parsedDate.format(format);
}

export function formatDateDMY(date, format = 'D MMMM YYYY') {
  if (!date) {
    console.log('No date provided:', date);
    return '';
  }

  const parsedDate = dayjs(date);
  if (!parsedDate.isValid()) {
    return 'Invalid time value';
  }

  return parsedDate.format(format);
}

export function formatTimeRemainder(date) {
  if (!date) {
    console.log('No date provided:', date);
    return '';
  }

  const parsedDate = dayjs(date);
  if (!parsedDate.isValid()) {
    return 'Invalid time value';
  }

  // Format the date as hh:mm A (e.g., 11:47 AM)
  return parsedDate.format('hh:mm A');
}

export function formatDateProject(date, format = 'D MMM YYYY') {
  if (!date) {
    console.log('No date provided:', date);
    return '';
  }

  const parsedDate = dayjs(date);
  if (!parsedDate.isValid()) {
    return 'Invalid time value';
  }

  return parsedDate.format(format);
}

export function formatDateGanttChart(date) {
  if (!date) return null;

  const d = new Date(date);
  return d.toISOString().split('T')[0];
}

export function formattedDate(date, format = 'YYYY-MM-DDTHH:mm:ss') {
  if (!date) {
    console.log('No date provided:', date);
    return null;
  }

  const parsedDate = dayjs(date);
  if (!parsedDate.isValid()) {
    return null;
  }

  return parsedDate.format(format);
}

export const formatStr = {
  dateTime: 'D MMMM YYYY, h:mm A', // 17 Apr 2022 12:00 am
  date: 'DD MMM YYYY', // 17 Apr 2022
  time: 'h:mm A', // 12:00 am
  split: {
    dateTime: 'DD/MM/YYYY h:mm A', // e.g., 17/04/2022 12:00 AM
    date: 'DD/MM/YYYY', // 17/04/2022
  },
  paramCase: {
    dateTime: 'DD-MM-YYYY h:mm A', // e.g., 17-04-2022 12:00 AM
    date: 'DD-MM-YYYY', // 17-04-2022
  },
};

export function today(format) {
  return dayjs(new Date()).startOf('day').format(format);
}

// ----------------------------------------------------------------------

/** output: 17 Apr 2022 12:00 am
 */
export function fDateTime(date, format) {
  if (!date) {
    return null;
  }

  const isValid = dayjs(date).isValid();

  return isValid ? dayjs(date).format(format ?? formatStr.dateTime) : 'Invalid time value';
}

// ----------------------------------------------------------------------

/** output: 17 Apr 2022
 */
export function fDate(date, format) {
  if (!date) {
    return null;
  }

  const isValid = dayjs(date).isValid();

  return isValid ? dayjs(date).format(format ?? formatStr.date) : 'Invalid time value';
}

// ----------------------------------------------------------------------

/** output: 12:00 am
 */
export function fTime(date, format) {
  if (!date) {
    return null;
  }

  const isValid = dayjs(date).isValid();

  return isValid ? dayjs(date).format(format ?? formatStr.time) : 'Invalid time value';
}

// ----------------------------------------------------------------------

/** output: 1713250100
 */
export function fTimestamp(date) {
  if (!date) {
    return null;
  }

  const isValid = dayjs(date).isValid();

  return isValid ? dayjs(date).valueOf() : 'Invalid time value';
}

// ----------------------------------------------------------------------

/** output: a few seconds, 2 years
 */
export function fToNow(date) {
  if (!date) {
    return null;
  }

  const isValid = dayjs(date).isValid();

  return isValid ? dayjs(date).toNow(true) : 'Invalid time value';
}

// ----------------------------------------------------------------------

/** output: boolean
 */
export function fIsBetween(inputDate, startDate, endDate) {
  if (!inputDate || !startDate || !endDate) {
    return false;
  }

  const formattedInputDate = fTimestamp(inputDate);
  const formattedStartDate = fTimestamp(startDate);
  const formattedEndDate = fTimestamp(endDate);

  if (formattedInputDate && formattedStartDate && formattedEndDate) {
    return formattedInputDate >= formattedStartDate && formattedInputDate <= formattedEndDate;
  }

  return false;
}

// ----------------------------------------------------------------------

/** output: boolean
 */
export function fIsAfter(startDate, endDate) {
  return dayjs(startDate).isAfter(endDate);
}

// ----------------------------------------------------------------------

/** output: boolean
 */
export function fIsSame(startDate, endDate, units) {
  if (!startDate || !endDate) {
    return false;
  }

  const isValid = dayjs(startDate).isValid() && dayjs(endDate).isValid();

  if (!isValid) {
    return 'Invalid time value';
  }

  return dayjs(startDate).isSame(endDate, units ?? 'year');
}

// ----------------------------------------------------------------------

/** output:
 * Same day: 26 Apr 2024
 * Same month: 25 - 26 Apr 2024
 * Same month: 25 - 26 Apr 2024
 * Same year: 25 Apr - 26 May 2024
 */
export function fDateRangeShortLabel(startDate, endDate, initial) {
  const isValid = dayjs(startDate).isValid() && dayjs(endDate).isValid();

  const isAfter = fIsAfter(startDate, endDate);

  if (!isValid || isAfter) {
    return 'Invalid time value';
  }

  let label = `${fDate(startDate)} - ${fDate(endDate)}`;

  if (initial) {
    return label;
  }

  const isSameYear = fIsSame(startDate, endDate, 'year');
  const isSameMonth = fIsSame(startDate, endDate, 'month');
  const isSameDay = fIsSame(startDate, endDate, 'day');

  if (isSameYear && !isSameMonth) {
    label = `${fDate(startDate, 'DD MMM')} - ${fDate(endDate)}`;
  } else if (isSameYear && isSameMonth && !isSameDay) {
    label = `${fDate(startDate, 'DD')} - ${fDate(endDate)}`;
  } else if (isSameYear && isSameMonth && isSameDay) {
    label = `${fDate(endDate)}`;
  }

  return label;
}

export function fDateRangeLabel(startDate, endDate, initial) {
  const start = dayjs(startDate);
  const end = dayjs(endDate);

  const isStartValid = start.isValid();
  const isEndValid = end.isValid();

  // If neither date is valid, return default text
  if (!isStartValid && !isEndValid) {
    return 'Invalid time value';
  }

  // If only one date is selected, return that date
  if (isStartValid && !isEndValid) {
    return fDate(startDate);
  }
  if (!isStartValid && isEndValid) {
    return fDate(endDate);
  }

  // Ensure the start date is before the end date
  if (fIsAfter(startDate, endDate)) {
    return 'Invalid time value';
  }

  let label = `${fDate(startDate)} - ${fDate(endDate)}`;

  if (initial) {
    return label;
  }

  const isSameYear = fIsSame(startDate, endDate, 'year');
  const isSameMonth = fIsSame(startDate, endDate, 'month');
  const isSameDay = fIsSame(startDate, endDate, 'day');

  if (isSameYear && !isSameMonth) {
    label = `${fDate(startDate, 'DD MMM')} - ${fDate(endDate)}`;
  } else if (isSameYear && isSameMonth && !isSameDay) {
    label = `${fDate(startDate, 'DD')} - ${fDate(endDate)}`;
  } else if (isSameYear && isSameMonth && isSameDay) {
    label = `${fDate(endDate)}`;
  }

  return label;
}

/** output: '2024-05-28T05:55:31+00:00'
 */
export function fAdd({
  years = 0,
  months = 0,
  days = 0,
  hours = 0,
  minutes = 0,
  seconds = 0,
  milliseconds = 0,
}) {
  const result = dayjs()
    .add(
      dayjs.duration({
        years,
        months,
        days,
        hours,
        minutes,
        seconds,
        milliseconds,
      })
    )
    .format();

  return result;
}

/** output: '2024-05-28T05:55:31+00:00'
 */
export function fSub({
  years = 0,
  months = 0,
  days = 0,
  hours = 0,
  minutes = 0,
  seconds = 0,
  milliseconds = 0,
}) {
  const result = dayjs()
    .subtract(
      dayjs.duration({
        years,
        months,
        days,
        hours,
        minutes,
        seconds,
        milliseconds,
      })
    )
    .format();

  return result;
}

/**
 * Returns a short label for the given date.
 *
 * If the 'initial' parameter is true, it can return a different format or part of the date.
 *
 * @param {Date | string | number} date - The date to format.
 * @param {boolean} initial - Optional. If true, return only a short version of the date.
 * @returns {string | null} - A short formatted label for the date, or null if the date is invalid.
 */
export function fDateShortLabel(date, initial = false) {
  if (!date) {
    return null;
  }

  const isValid = dayjs(date).isValid();

  if (!isValid) {
    return 'Invalid time value';
  }

  // Return different formats based on the 'initial' flag
  return initial ? dayjs(date).format('MMM DD') : dayjs(date).format('DD MMM YYYY');
}
